import React, { useState, useCallback, useEffect, useRef, memo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { array, object, oneOfType } from 'prop-types';
import {
    useStylitics,
    openMiniPDP,
    styliticsLogger,
} from 'yoda-core-components/lib/helpers/Stylitics';
import { dt } from 'yoda-core-components/lib/helpers/Utils/GetTailwindToken';
import {
    miniPDPOnDynamicGalleriesOpen,
    miniPdpAtcFromDG,
} from 'yoda-site-components/lib/actions/MiniPDPAction';
import {
    dynamicGalleriesLoadSuccess,
    dynamicGalleriesModuleClick,
} from '../../actions/AnalyticsActions';
import dynamicGalleriesConfig from './configs/dynamicGalleries.config';

const STYLE_OVERRIDES = `
.stylitics-ui-carousel-outer { font-family: "Open Sans" !important; }
.stylitics-context-action { color: #666666 !important; line-height: 20px !important; }
.stylitics-context-item { color: #202020 !important; font-weight: 600 !important; line-height: 20px !important; }
.stylitics-dynamic-galleries-card-footer { color: #202020 !important; font-size: 16px !important; font-weight: 700 !important; line-height: 24px !important; }
.stylitics-dynamic-galleries-modal-heading { color: #202020 !important; font-size: 16px !important; font-weight: 600 !important; line-height: 24px !important; }
.stylitics-ui-item-info-details { color: #202020 !important; font-family: "Arial" !important; font-weight: 400 !important; }
.stylitics-item-regular-price { color: #202020 !important; font-family: "Arial" !important; font-size: 14px !important; font-weight: 700 !important; }
.stylitics-item-regular-price-slashed { color: #666666 !important; font-family: "Arial" !important; font-size: 14px !important; }
.stylitics-item-sale-price { font-family: "Arial" !important; font-size: 14px !important; font-weight: 700 !important; }
.promotional-sale-price { color: #E71324 !important; font-family: "Arial" !important; font-size: 14px !important; font-weight: 700 !important; }
.promotional-text { font-style: italic !important; font-weight: 400 !important; }
`;

const DynamicGalleriesWidget = ({ products }) => {
    const dispatch = useDispatch();
    const {
        context: {
            featureFlags: { enableFallbackIDs = true } = {},
            messagesTexts: { dynamicGalleriesWidget: { cardFooterText = '' } = {} } = {},
            preferences = {},
            requestUrl = '',
        } = {},
        productList: { galleryDetails: { breadCrumbInfo = [] } = {} } = {},
        miniPDP = {},
    } = useSelector((store) => store);

    const prevMiniPDPRef = useRef(miniPDP);
    const [hasContent, setHasContent] = useState(false);
    const catId = breadCrumbInfo[1]?.catId || '';

    const handleOutfitsLoaded = useCallback(
        (outfits) => {
            if (outfits?.length > 0) {
                setHasContent(true);
                dispatch(dynamicGalleriesLoadSuccess(window.digitalData));
            }
        },
        [dispatch]
    );

    const handleItemClick = useCallback(
        (item, outfit) => {
            const opts = openMiniPDP(item);
            if (opts) {
                dispatch(miniPDPOnDynamicGalleriesOpen(item?.item_id || ''));
                dispatch(
                    dynamicGalleriesConfig.analyticsClickAction({
                        id: outfit?.id,
                        ppId: opts.ppId,
                    })
                );
            }
        },
        [dispatch]
    );

    const handleBundleClick = useCallback(
        (bundle) => {
            dispatch(
                dynamicGalleriesModuleClick({
                    dynamicGalleryID: bundle?.id?.toString() || '',
                })
            );
        },
        [dispatch]
    );

    const handleError = useCallback((err) => {
        styliticsLogger.error('DynamicGalleries init failed:', err.message);
    }, []);

    // Extend config with widget-specific params
    const config = {
        ...dynamicGalleriesConfig,
        createWidget: (itemNumber, featureFlags) =>
            dynamicGalleriesConfig.createWidget(itemNumber, featureFlags, {
                catId,
                requestUrl,
                products,
                enableFallbackIDs,
                cardFooterText,
            }),
        registerEvents: (instance, callbacks) =>
            dynamicGalleriesConfig.registerEvents(instance, {
                ...callbacks,
                onBundleClick: handleBundleClick,
            }),
    };

    useStylitics(config, {
        preferences,
        featureFlags: { enableFallbackIDs },
        onOutfitsLoaded: handleOutfitsLoaded,
        onItemClick: handleItemClick,
        onError: handleError,
    });

    useEffect(() => {
        const style = document.createElement('style');
        style.textContent = STYLE_OVERRIDES;
        document.head.appendChild(style);
        return () => document.head.removeChild(style);
    }, []);

    useEffect(() => {
        const prev = prevMiniPDPRef.current;
        if (miniPDP?.miniPDPDGATC && !prev?.miniPDPDGATC) {
            dispatch(miniPdpAtcFromDG(false));
        }
        prevMiniPDPRef.current = miniPDP;
    }, [miniPDP, dispatch]);

    useEffect(() => {
        return () => {
            const { attributes = {} } = window?.digitalData?.page || {};
            delete attributes?.dynamicGalleryModule;
        };
    }, []);

    return (
        <div
            className={hasContent ? dt(['mb-6']) : undefined}
            id="dynamic-galleries-widget-container"
        />
    );
};

DynamicGalleriesWidget.defaultProps = {
    products: [],
};

DynamicGalleriesWidget.propTypes = {
    products: oneOfType([array, object]),
};

export default memo(DynamicGalleriesWidget);
